/**
 * viewportValues.js — числа снимка в окне Cornerstone: откуда их брать. [12.0.57]
 *
 * Вынесено из ui/specModes.js, потому что нужно и ядру: лупа (core/viewportUI)
 * показывает значение под перекрестием, а ядро не должно звать интерфейс.
 *
 * ПОВОД — стенд «Спец. режимы» (tests/стенды/specrezhimy): значения снимка во
 * вкладке брались uiManager._getVolumeScalarData — в окнах MPR на Cornerstone
 * 5.6 она пуста (у актёров нет скаляров, window.cornerstone программа не
 * заводит). Лупа при этом звала worldToIndex у объекта, у которого такого
 * метода нет, и подпись под ней была «— HU» всегда.
 *
 * Стопка — текущий снимок (vp.getImageData(): его voxelManager, размеры
 * [w, h, 1], начало и оси снимка); реформат — его том из кэша по имени
 * (getVolumeId / referencedId актёра, не uid — 12.0.54, §3). Значения
 * читаются по вокселю (voxelManager.getAtIndex), без копии тома.
 *
 * EXPORTS: данныеОкна, плоскостьОкна, номерСреза, модальностьОкна, значениеПодТочкой
 */
import { getRenderingEngine, cornerstone, ViewportType } from './viewportState.js';
import { ПРЕФИКС } from './volumePick.js';
import { значениеВТочке } from './specModesCore.js';

/** Имя тома реформата: актёр знает его как referencedId, не как uid (12.0.54, §3). */
function томОкна(vp) {
  let id = null;
  try { id = vp.getVolumeId?.() || null; } catch (e) { id = null; }
  if (!id) { const а = (vp.getActors?.() || [])[0]; id = а?.referencedId || null; }
  if (!id) return null;
  const cache = cornerstone.cache;
  return cache.getVolume(id) || cache.getVolume(String(id).startsWith(ПРЕФИКС) ? String(id).slice(ПРЕФИКС.length) : ПРЕФИКС + id) || null;
}

/**
 * Значения окна в мире: стопка — текущий снимок (dims [w, h, 1]),
 * реформат — его том. Для чтения — получить(линейный индекс).
 * @returns {{ok:true, данные:object, модальность:string, тип:string}|{ok:false, причина:string}}
 */
export function данныеОкна(vpOrId) {
  const vp = typeof vpOrId === 'string' ? getRenderingEngine()?.getViewport(vpOrId) : vpOrId;
  if (!vp) return { ok: false, причина: 'окна нет' };
  if (vp.type === ViewportType.VOLUME_3D) return { ok: false, причина: 'в объёмном 3D-окне среза нет — щёлкните по окну со срезом' };
  try {
    if (vp.type === ViewportType.STACK) {
      const и = vp.getImageData?.();
      const vm = и?.voxelManager;
      if (!и || !vm) return { ok: false, причина: 'снимок ещё не загружен — подождите и повторите' };
      const dims = [и.dimensions[0], и.dimensions[1], 1];
      return {
        ok: true, тип: 'стопка', модальность: String(и.metadata?.Modality || '').toUpperCase(),
        данные: { dims, spacing: Array.from(и.spacing), origin: Array.from(и.origin), direction: Array.from(и.direction), получить: (i) => vm.getAtIndex(i) },
      };
    }
    const том = томОкна(vp);
    const vm = том?.voxelManager;
    if (!том || !vm) return { ok: false, причина: 'объём этого окна ещё не загружен — подождите и повторите' };
    return {
      ok: true, тип: 'реформат', модальность: String(том.metadata?.Modality || '').toUpperCase(),
      данные: { dims: Array.from(том.dimensions), spacing: Array.from(том.spacing), origin: Array.from(том.origin), direction: Array.from(том.direction), получить: (i) => vm.getAtIndex(i) },
      том,
    };
  } catch (e) {
    return { ok: false, причина: 'значения снимка не читаются: ' + e.message };
  }
}

/** Плоскость среза окна: точка фокуса, нормаль и «верх» камеры. */
export function плоскостьОкна(vp) {
  const c = vp.getCamera();
  return { точка: Array.from(c.focalPoint), нормаль: Array.from(c.viewPlaneNormal), верх: Array.from(c.viewUp || [0, 1, 0]) };
}

/** Номер среза окна (с единицы) и сколько их всего. */
export function номерСреза(vp) {
  try {
    if (vp.type === ViewportType.STACK) return { номер: (vp.getCurrentImageIdIndex?.() ?? 0) + 1, всего: vp.getImageIds?.()?.length || 0 };
    return { номер: (vp.getSliceIndex?.() ?? 0) + 1, всего: vp.getNumberOfSlices?.() || 0 };
  } catch (e) { return { номер: null, всего: null }; }
}

/** Модальность окна (CT, MR…) — из его данных. */
export function модальностьОкна(vp) {
  const д = данныеОкна(vp);
  return д.ok ? д.модальность : '';
}

/**
 * Значение снимка под точкой холста окна (CSS-пиксели от левого верхнего угла).
 * @returns {{значение:number, модальность:string}|null}
 */
export function значениеПодТочкой(vp, xy) {
  const д = данныеОкна(vp);
  if (!д.ok) return null;
  const v = значениеВТочке(д.данные, vp.canvasToWorld(xy));
  return v == null ? null : { значение: v, модальность: д.модальность };
}

export default { данныеОкна, плоскостьОкна, номерСреза, модальностьОкна, значениеПодТочкой };
